import { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import waterpipe from "waterpipe";
import { classNames, DataView, useMemoizedFunction } from "zeta-dom-react";
import { useTranslation } from "src/i18n";
import { useBreakpoint } from "src/util/cssUtil";
import { Dropdown } from "src/components/controls";
import { Icon } from ".";

interface PaginationProps {
    dataView?: DataView<any>;
    itemCount?: number;
    pageSize?: number;
    pageCount?: number;
    currentPage?: number;
    onPageChange?: (index: number) => void;
}

export function Pagination(props: PaginationProps) {
    const { t } = useTranslation('common');
    const { isMobile } = useBreakpoint();
    const dataView = props.dataView;
    const itemCount = props.itemCount ?? dataView?.itemCount ?? 0;
    const pageSize = props.pageSize ?? dataView?.pageSize ?? 0;
    const pageCount = props.pageCount ?? (pageSize ? Math.ceil(itemCount / pageSize) : 1);
    const [pageIndex, setPageIndex] = useState(0);

    const onPageChange = useMemoizedFunction((index: number) => {
        setPageIndex(index);
        props.onPageChange?.(index);
        if (dataView) {
            dataView.pageIndex = index;
        }
    });

    useEffect(() => {
        if (props.currentPage !== undefined) {
            setPageIndex(props.currentPage);
        } else if (dataView?.pageIndex !== undefined) {
            setPageIndex(dataView.pageIndex);
        }
    }, [props.currentPage, dataView?.pageIndex]);

    useEffect(() => {
        if (pageIndex >= pageCount) {
            onPageChange(Math.max(0, pageCount - 1));
        }
    }, [pageIndex, pageCount, onPageChange]);

    if (pageCount <= 1) {
        return null;
    }
    return (
        <div className="eac-pagination">
            {pageCount > 5 && isMobile ?
                <div className="eac-pagination-dropdown">
                    <Icon icon="arrowLeft" onClick={() => onPageChange(Math.max(0, pageIndex - 1))} className={classNames({ disabled: pageIndex <= 0 })} />
                    <span>{t('pagination.leadingText', { pageCount })}</span>
                    <Dropdown items={waterpipe.eval('1 .. @0', pageCount)} value={pageIndex + 1}
                        onChange={v => onPageChange((v || 1) - 1)} />
                    <span>{t('pagination.trailingText', { pageCount })}</span>
                    <Icon icon="arrowRight" onClick={() => onPageChange(Math.min(pageCount - 1, pageIndex + 1))} className={classNames({ disabled: pageIndex >= pageCount - 1 })} />
                </div> :
                <ReactPaginate
                    containerClassName="eac-pagination-list"
                    nextLabel={<Icon icon="arrowRight" />}
                    previousLabel={<Icon icon="arrowLeft" />}
                    breakLabel={<Icon icon="dots" />}
                    pageRangeDisplayed={5}
                    marginPagesDisplayed={1}
                    activeClassName="active"
                    pageCount={pageCount}
                    forcePage={pageIndex}
                    onPageChange={e => onPageChange(e.selected)} />}
        </div>
    );
}
